/**
 * WorkspaceGate — Patch 34.1.12.
 *
 * Wraps any workspace-aware page in the four-phase state contract from
 * ``useWorkspaceState``. Never redirects to ``/welcome`` from a
 * transient loading state — that's what caused the founder to see the
 * "Orkexra → Select a workspace → Orkexra" flash.
 *
 * Usage:
 *
 *     <WorkspaceGate>
 *       {(ws) => <CampaignRoomView workspace={ws} />}
 *     </WorkspaceGate>
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useWorkspaceState } from "@/entities/workspace/hooks";
import type { WorkspaceState } from "@/entities/workspace/hooks";

type Workspace = NonNullable<WorkspaceState["workspace"]>;

interface Props {
  children: (workspace: Workspace) => ReactNode;
  /** Optional skeleton for the ``loading`` phase. Defaults to a page-height shimmer. */
  loading?: ReactNode;
  /** Optional CTA for the ``missing`` phase. Defaults to "Create workspace". */
  missing?: ReactNode;
}

export function WorkspaceGate({ children, loading, missing }: Props): JSX.Element {
  const { phase, workspace, error } = useWorkspaceState();

  if (phase === "loading") {
    return (
      <div data-testid="workspace-gate-loading" className="mx-auto max-w-3xl px-6 py-16">
        {loading ?? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-1/3 rounded bg-[var(--md-ink-2,#1a1f28)]" />
            <div className="h-4 w-2/3 rounded bg-[var(--md-ink-2,#1a1f28)]" />
            <div className="mt-8 h-40 w-full rounded bg-[var(--md-ink-2,#1a1f28)]" />
          </div>
        )}
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div
        data-testid="workspace-gate-error"
        className="mx-auto max-w-2xl px-6 py-24 text-center"
      >
        <h2 className="text-2xl font-semibold mb-3">
          We hit a snag loading your workspace.
        </h2>
        <p className="text-muted-foreground mb-6 text-sm">
          {(error as { message?: string })?.message ?? "The server is temporarily unavailable."}
        </p>
        <button
          type="button"
          data-testid="workspace-gate-retry"
          onClick={() => window.location.reload()}
          className="md-btn md-btn--primary inline-flex"
        >
          Retry
        </button>
      </div>
    );
  }

  if (phase === "missing" || !workspace) {
    return (
      <div
        data-testid="workspace-gate-missing"
        className="mx-auto max-w-2xl px-6 py-24 text-center"
      >
        {missing ?? (
          <>
            <h2 className="text-2xl font-semibold mb-3">
              You don&apos;t have a workspace yet.
            </h2>
            <p className="text-muted-foreground mb-6 text-sm">
              Set one up in a minute to unlock campaigns, content, and publishing.
            </p>
            <Link
              to="/onboarding"
              data-testid="workspace-gate-create"
              className="md-btn md-btn--primary inline-flex"
            >
              Create your workspace
            </Link>
          </>
        )}
      </div>
    );
  }

  return <>{children(workspace)}</>;
}
